import { useEffect, useRef, useState } from 'react'
import type { DragEvent, KeyboardEvent } from 'react'

import css from './file-dropzone.module.scss'

const containsFiles = (transfer: DataTransfer) => Array.from(transfer.types || []).includes('Files')
    || transfer.files?.length > 0
    || Array.from(transfer.items || []).some(item => item.kind === 'file')

const droppedFiles = (transfer: DataTransfer): File[] => {
    const files = Array.from(transfer.files || [])
    const items = Array.from(transfer.items || []).filter(item => item.kind === 'file')
    const directories = items.map(item => {
        try {return item.webkitGetAsEntry?.()?.isDirectory === true}
        catch {return false}
    })
    if (!directories.some(Boolean)) return files
    // A directory is not an avatar. Do not enumerate its contents or start
    // asynchronous reads which could outlive the selected folder/upload state.
    return items.flatMap((item, index) => {
        if (directories[index]) return []
        const file = item.getAsFile()
        return file ? [file] : []
    })
}

export const AvatarFileDropzone = ({disabled, onFiles}: {
    disabled: boolean
    onFiles: (files: File[]) => void
}) => {
    const input = useRef<HTMLInputElement>(null)
    const dragDepth = useRef(0)
    const [dragging, setDragging] = useState(false)

    const clearDrag = () => {dragDepth.current = 0; setDragging(false)}
    useEffect(() => {if (disabled) {dragDepth.current = 0; setDragging(false)}}, [disabled])

    const chooseFiles = () => {if (!disabled) input.current?.click()}
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || !['Enter', ' ', 'Spacebar'].includes(event.key)) return
        event.preventDefault()
        event.stopPropagation()
        chooseFiles()
    }
    const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
        if (disabled) {clearDrag(); return}
        dragDepth.current += 1
        setDragging(true)
    }
    const onDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
    }
    const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (dragDepth.current === 0) return
        event.stopPropagation()
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragging(false)
    }
    const onDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!containsFiles(event.dataTransfer)) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
        clearDrag()
        if (disabled) return
        const files = droppedFiles(event.dataTransfer)
        if (files.length) onFiles(files)
    }

    return <div role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled}
        aria-label="Добавить аватары: выбрать файлы или перетащить их сюда"
        className={`${css.zone}${dragging ? ` ${css.dragging}` : ''}${disabled ? ` ${css.disabled}` : ''}`}
        onClick={chooseFiles} onKeyDown={onKeyDown}
        onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        <input ref={input} type="file" multiple accept="image/png,image/jpeg,image/webp"
            aria-label="Файлы аватаров" className={css.input} disabled={disabled} tabIndex={-1}
            onClick={event => event.stopPropagation()}
            onChange={event => {
                const files = Array.from(event.currentTarget.files || [])
                event.currentTarget.value = ''
                if (!disabled && files.length) onFiles(files)
            }} />
        <strong>{dragging ? 'Отпустите файлы, чтобы добавить их' : 'Перетащите изображения сюда'}</strong>
        <span>или нажмите, чтобы выбрать файлы</span>
    </div>
}
